import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellOff, X, Settings, CheckCheck, Inbox, ChevronDown, Home } from "lucide-react";
import { toast } from "sonner";
import { notificationsApi, householdsApi, formatError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { SwipeableAlertList } from "./SwipeableAlert";

/**
 * AlertsSheet — bottom sheet that lists the in-app notification feed.
 *
 * Multi-household behaviour:
 *  - Food alerts (type "expired" | "expiring") are grouped by household,
 *    each group rendered inside a collapsible bar with its own count
 *    badge. Tap the bar to expand/collapse the list.
 *  - The daily-reminder alert (type "reminder") is rendered SEPARATELY
 *    at the top of the sheet, since it doesn't belong to any household.
 *
 * Tapping an alert in a non-active household auto-switches to that
 * household before navigating, with a small toast for context.
 */
export const AlertsSheet = ({
  open,
  onClose,
  alerts = [],
  enabled = true,
  loading = false,
  onAlertsChange,
  onMarkAllViewed,
}) => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  // Group food alerts by household_id; keep reminder alerts in a separate
  // bucket. Separate the active household's alerts from non-active ones so
  // the UI can render the active set flat (no bar) while non-active sets
  // ALWAYS get their own household bar — even if only one alert exists
  // there. This way the user can never confuse a single alert in the bell
  // as belonging to the active household when it's actually from another.
  const { reminderAlerts, activeAlerts, nonActiveGroups } = useMemo(() => {
    const activeId = user?.household_id;
    const reminders = [];
    const active = [];
    const byHh = new Map(); // non-active hid -> { id, name, alerts: [] }
    for (const a of alerts) {
      if (a.type === "reminder" || !a.household_id) {
        reminders.push(a);
        continue;
      }
      if (a.household_id === activeId) {
        active.push(a);
        continue;
      }
      if (!byHh.has(a.household_id)) {
        byHh.set(a.household_id, {
          id: a.household_id,
          name: a.household_name || "Household",
          alerts: [],
        });
      }
      byHh.get(a.household_id).alerts.push(a);
    }
    return {
      reminderAlerts: reminders,
      activeAlerts: active,
      nonActiveGroups: [...byHh.values()],
    };
  }, [alerts, user?.household_id]);

  // Track which non-active groups are expanded.
  const [collapsed, setCollapsed] = useState(() => new Set());
  React.useEffect(() => {
    // Default: collapse every non-active household bar so the bell isn't
    // a wall of text on open.
    setCollapsed(new Set(nonActiveGroups.map((g) => g.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonActiveGroups.map((g) => g.id).join(",")]);

  const toggleGroup = (hid) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(hid)) next.delete(hid);
      else next.add(hid);
      return next;
    });
  };

  const openSettings = () => {
    onClose?.();
    navigate("/notifications");
  };

  const goToContainer = async (alert) => {
    onClose?.();
    // If alert belongs to a non-active household, switch first.
    if (alert.household_id && alert.household_id !== user?.household_id) {
      try {
        const r = await householdsApi.switch(alert.household_id);
        await refresh();
        toast.success(`Switched to ${r.name}`);
      } catch (err) {
        toast.error(formatError(err));
        return;
      }
    }
    if (alert.container_id) navigate(`/container/${alert.container_id}`);
  };

  // Optimistic single-alert dismiss with Undo.
  const handleDeleteOne = async (alertId) => {
    const before = alerts;
    const next = before.filter((a) => a.id !== alertId);
    onAlertsChange?.(next);

    try {
      await notificationsApi.dismiss(alertId);
      toast.success("Alert dismissed", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await notificationsApi.restore([alertId]);
              onAlertsChange?.(before);
            } catch (err) {
              console.warn("Restore failed:", err);
              toast.error("Couldn't undo — try refreshing");
            }
          },
        },
      });
    } catch (err) {
      console.warn("Dismiss failed:", err);
      onAlertsChange?.(before);
      toast.error("Couldn't dismiss — try again");
    }
  };

  // Mark-all-as-viewed: NO LONGER deletes alerts. We just notify the parent
  // so it can persist the IDs as "viewed" (clears the bell badge over the
  // navbar) — the alerts themselves stay rendered in the sheet so the user
  // can still review them. New alerts that materialise later will re-badge.
  const handleMarkAllViewed = () => {
    if (alerts.length === 0) return;
    const ids = alerts.map((a) => a.id);
    onMarkAllViewed?.(ids);
    toast.success(`Cleared badge for ${ids.length} alert${ids.length === 1 ? "" : "s"}`);
  };

  const totalAlerts = alerts.length;
  const hasNonActiveGroups = nonActiveGroups.length > 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[55] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center"
          data-testid="alerts-sheet-overlay"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-5 sm:m-4 max-h-[82vh] overflow-y-auto"
            data-testid="alerts-sheet"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl brand-gradient-bg flex items-center justify-center">
                  {enabled ? (
                    <Bell className="w-4 h-4 text-white" />
                  ) : (
                    <BellOff className="w-4 h-4 text-white" />
                  )}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
                    Alerts
                  </div>
                  <div className="font-heading text-lg font-semibold text-[#2D3142]">
                    Your fridge feed
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={openSettings}
                  data-testid="alerts-settings-btn"
                  className="w-9 h-9 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
                  aria-label="Notification settings"
                >
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={onClose}
                  data-testid="alerts-close-btn"
                  className="w-9 h-9 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {enabled && totalAlerts > 0 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#8A93A6]">
                  {totalAlerts} alert{totalAlerts === 1 ? "" : "s"}{hasNonActiveGroups && " · other households grouped"}
                </div>
                <button
                  type="button"
                  onClick={handleMarkAllViewed}
                  data-testid="alerts-mark-all-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#277A82] hover:text-[#1F5566] px-2 py-1 rounded-md hover:bg-[#DCEDF0]/60 transition-colors"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Mark all as viewed
                </button>
              </div>
            )}

            {!enabled ? (
              <div className="mt-6 tup-card p-6 text-center" data-testid="alerts-disabled">
                <BellOff className="w-7 h-7 mx-auto text-[#8A93A6]" />
                <div className="font-heading text-base font-semibold text-[#2D3142] mt-3">
                  Notifications are off
                </div>
                <p className="text-xs text-[#4F5D75] mt-2 leading-relaxed">
                  Turn them on in settings to see expiry alerts and reminders here.
                </p>
                <button
                  onClick={openSettings}
                  data-testid="alerts-enable-btn"
                  className="mt-4 inline-flex items-center justify-center gap-1.5 brand-gradient-bg text-white text-sm font-semibold rounded-xl px-4 py-2.5"
                >
                  <Settings className="w-3.5 h-3.5" /> Open settings
                </button>
              </div>
            ) : loading ? (
              <div className="mt-6 tup-card p-6 text-center text-sm text-[#8A93A6]">Loading…</div>
            ) : totalAlerts === 0 ? (
              <div className="mt-6 tup-card p-6 text-center" data-testid="alerts-empty">
                <Inbox className="w-7 h-7 mx-auto text-[#8A93A6]" />
                <div className="font-heading text-base font-semibold text-[#2D3142] mt-3">
                  All clear
                </div>
                <p className="text-xs text-[#4F5D75] mt-2 leading-relaxed">
                  Nothing needs your attention right now. We'll let you know when something is about to expire.
                </p>
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                {/* Daily-reminder alerts (no household). Always rendered at the
                    top in their own bucket so they don't get nested under a
                    household bar. */}
                {reminderAlerts.length > 0 && (
                  <SwipeableAlertList
                    alerts={reminderAlerts}
                    onOpenContainer={() => onClose?.()}
                    onDelete={handleDeleteOne}
                  />
                )}

                {/* Active-household alerts: render flat (no bar). The user is
                    currently viewing this household, so the household
                    context is already implicit. */}
                {activeAlerts.length > 0 && (
                  <SwipeableAlertList
                    alerts={activeAlerts}
                    onOpenContainer={(cid) => {
                      const a = activeAlerts.find((x) => x.container_id === cid);
                      if (a) goToContainer(a);
                    }}
                    onDelete={handleDeleteOne}
                  />
                )}

                {/* Non-active households: ALWAYS wrapped in a collapsible
                    household bar — even if there's only one alert there —
                    so the user can never confuse a stray alert as belonging
                    to the active household. */}
                {nonActiveGroups.map((g) => {
                  const isCollapsed = collapsed.has(g.id);
                  return (
                    <div key={g.id} data-testid={`alerts-group-${g.id}`}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(g.id)}
                        data-testid={`alerts-group-toggle-${g.id}`}
                        aria-expanded={!isCollapsed}
                        className={`w-full tup-card p-3 flex items-center gap-3 transition-colors ${
                          g.alerts.some((a) => a.severity === "high")
                            ? "border-l-4 border-[#E07A5F]"
                            : "border-l-4 border-[#E2C76F]"
                        } hover:bg-[#F5F3ED]`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-[#DCEDF0] text-[#1F5566] flex items-center justify-center shrink-0">
                          <Home className="w-4 h-4" />
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <div className="font-semibold text-sm text-[#2D3142] truncate">
                            {g.name}
                          </div>
                          <div className="text-[10px] text-[#8A93A6] uppercase tracking-wider">
                            {g.alerts.length} alert{g.alerts.length === 1 ? "" : "s"} · not active
                          </div>
                        </div>
                        <span
                          className="text-xs font-bold text-white bg-[#E07A5F] rounded-full min-w-[1.5rem] h-6 px-2 flex items-center justify-center shrink-0"
                          data-testid={`alerts-group-count-${g.id}`}
                        >
                          {g.alerts.length}
                        </span>
                        <motion.div
                          animate={{ rotate: isCollapsed ? 0 : 180 }}
                          transition={{ duration: 0.2 }}
                          className="shrink-0 text-[#4F5D75]"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </motion.div>
                      </button>
                      <AnimatePresence initial={false}>
                        {!isCollapsed && (
                          <motion.div
                            key="content"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22 }}
                            className="overflow-hidden"
                            data-testid={`alerts-group-content-${g.id}`}
                          >
                            <div className="pt-2 pl-2">
                              <SwipeableAlertList
                                alerts={g.alerts}
                                onOpenContainer={(cid) => {
                                  const a = g.alerts.find((x) => x.container_id === cid);
                                  if (a) goToContainer(a);
                                }}
                                onDelete={handleDeleteOne}
                              />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
